import { marked } from "marked";
import DOMPurify from "dompurify";

// SVG icon markup for copy buttons in code blocks (rendered via innerHTML)
const COPY_ICON_SVG = `<svg class="copy-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect x="9" y="9" width="13" height="13" rx="2" ry="2"/><path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/></svg>`;
const CHECK_ICON_SVG = `<svg class="check-icon" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><polyline points="20 6 9 17 4 12"/></svg>`;

const escapeAttr = (s: string) =>
  s
    .replace(/&/g, "&amp;")
    .replace(/"/g, "&quot;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");

// `marked` is a singleton: configuring it here, beside renderMarkdown, is what
// makes every caller (activity thread, chat, streaming) render the same way.
// The copy-button chrome used to be emitted here too, but that made it part
// of the untrusted string DOMPurify sanitises — chrome now goes on after
// sanitising (see wrapCodeBlocks), so this renderer only owns the fence
// itself. The fence body is still escaped *here*: unescaped fenced markup
// became live DOM and covered the item page (FLOW-805), and that risk is
// independent of the raw-HTML profile fixed below (FLOW-807).
marked.use({
  renderer: {
    link({ href, title, text }) {
      const titleAttr = title ? ` title="${escapeAttr(title)}"` : "";
      return `<a href="${escapeAttr(href)}"${titleAttr} target="_blank" rel="noopener noreferrer">${text}</a>`;
    },
    code({ text, lang, escaped }) {
      const langClass = lang ? ` class="language-${escapeAttr(lang)}"` : "";
      const body = escaped ? text : escapeAttr(text);
      return `<pre><code${langClass}>${body}</code></pre>`;
    },
  },
});

// FLOW-807: USE_PROFILES: { html: true } kept div/form/input/button/style/
// class/role/data-* — enough for a raw (unfenced) HTML block in a comment to
// mount a fixed-position overlay or a credential-phishing form. This is an
// explicit allow-list instead: no div, no form, no style, no role/aria-*,
// no data-*. `target` is deliberately absent — it was already stripped under
// the old profile, and restoring it is a behaviour change this item does not
// own (FLOW-805).
const ALLOWED_TAGS = [
  "p",
  "br",
  "hr",
  "strong",
  "em",
  "del",
  "code",
  "pre",
  "blockquote",
  "a",
  "span",
  "ul",
  "ol",
  "li",
  "input",
  "h1",
  "h2",
  "h3",
  "h4",
  "h5",
  "h6",
  "table",
  "thead",
  "tbody",
  "tr",
  "th",
  "td",
  "img",
  "details",
  "summary",
];

const ALLOWED_ATTR = [
  "href",
  "title",
  "rel",
  "src",
  "alt",
  "class",
  "type",
  "disabled",
  "checked",
  "align",
  "colspan",
  "rowspan",
];

// `class` is in ALLOWED_ATTR because GFM fenced code needs `language-x` and
// task lists render through ordinary `li`/`input` markup, but Tailwind
// utilities are global: `<div class="fixed inset-0 z-50">` is a page-covering
// overlay with no `style` attribute at all, so DOMPurify's attribute
// allow-list (which only knows attribute *names*) can't stop it. This regex
// keeps only tokens that look like a highlight.js language class and drops
// the rest of the value, so an author can select a fence language but not an
// arbitrary utility class.
const LANGUAGE_CLASS = /^language-[\w.+-]{1,32}$/;

function filterLanguageClasses(doc: Document): void {
  doc.body.querySelectorAll("[class]").forEach((el) => {
    const kept = (el.getAttribute("class") ?? "")
      .split(/\s+/)
      .filter((token) => LANGUAGE_CLASS.test(token));
    if (kept.length > 0) {
      el.setAttribute("class", kept.join(" "));
    } else {
      el.removeAttribute("class");
    }
  });
}

// `input` has to stay in ALLOWED_TAGS for GFM task lists, which compile to
// exactly `<input type="checkbox" disabled>` — so it's narrowed here rather
// than banned outright. Anything else (password fields, hidden fields, a
// submit button smuggled in as `input[type=submit]`) is removed.
function stripUnsafeInputs(doc: Document): void {
  doc.body.querySelectorAll("input").forEach((input) => {
    const isTaskListCheckbox =
      input.getAttribute("type") === "checkbox" &&
      input.hasAttribute("disabled");
    if (!isTaskListCheckbox) input.remove();
  });
}

// Adds the copy-button chrome around every `pre`. This runs last, and that
// order is load-bearing: it comes after the class filter, so an author
// cannot forge `code-block-wrapper` / `copy-code-btn` classes on their own
// markup and have them survive; and it comes after DOMPurify, so the chrome
// (a static constant with no interpolation) is never itself sanitised away —
// `USE_PROFILES: { html: true }` never allowed SVG, so these icons were
// silently stripped on every surface until now.
function wrapCodeBlocks(doc: Document): void {
  doc.body.querySelectorAll("pre").forEach((pre) => {
    // The NodeList is static, so a `pre` nested inside another one is still
    // visited after its parent was wrapped. Wrapping it again would nest the
    // chrome and leave two copy buttons whose `closest` lookups disagree.
    if (pre.closest(".code-block-wrapper")) return;
    const wrapper = doc.createElement("div");
    wrapper.className = "code-block-wrapper";
    wrapper.innerHTML = `<button type="button" class="copy-code-btn" aria-label="Copy code" title="Copy code">${COPY_ICON_SVG}${CHECK_ICON_SVG}</button>`;
    pre.replaceWith(wrapper);
    wrapper.appendChild(pre);
  });
}

/**
 * Render a markdown string to sanitised HTML suitable for `innerHTML`.
 *
 * Use this for any user-authored content (e.g. activity-thread comments).
 * Configures DOMPurify to strip script tags, event handlers, and
 * javascript: URLs; preserves common safe markdown output.
 */
export function renderMarkdown(input: string): string {
  if (!input) return "";
  const rawHtml = marked.parse(input, { async: false }) as string;
  const clean = DOMPurify.sanitize(rawHtml, {
    ALLOWED_TAGS,
    ALLOWED_ATTR,
    ALLOW_DATA_ATTR: false,
    ALLOW_ARIA_ATTR: false,
  });

  // A single module-private DOM pass, not a DOMPurify hook — hooks mutate the
  // shared DOMPurify singleton globally, which would leak this module's rules
  // onto any other caller of DOMPurify. The three steps must run in this
  // order: language-class filtering and checkbox narrowing both apply to
  // *every* element, including ones the chrome step is about to add, so they
  // run first; the chrome is added last so its own classes are never subject
  // to the filter that was just run.
  const doc = new DOMParser().parseFromString(clean, "text/html");
  filterLanguageClasses(doc);
  stripUnsafeInputs(doc);
  wrapCodeBlocks(doc);
  return doc.body.innerHTML;
}
